/**
 * The poster surfaces' furniture (reference build, 2026-08-22).
 *
 * Four screens in this app are PRINTED rather than rendered — the sign-in, both
 * onboarding runs, and (server-side, in apps/api/src/pages) the public door and
 * the waiting room. They share a yellow ground, a dashed page frame with a
 * registration cross at each corner, cream plates with hard offset-ink shadows,
 * brass small-caps labels and the display face.
 *
 * Everything here is shape, not colour: the colours are in styles/poster.css,
 * which is where the whole look can be read in one sitting. These components
 * exist so that a screen says `<PosterPage>` and gets the frame, the marks and
 * the column without four screens each remembering to.
 *
 * FIXED LIGHT. `.nob-poster` repaints the role variables for paper and sets
 * `color-scheme: light`, so none of this follows `[data-theme]`. That is the
 * point: a poster that changed colour with the reader's laptop would be a
 * different poster, and a stranger's first view of the product would not be one
 * thing.
 */
import type { ReactNode } from "react";
import { NobFace, type NobMood } from "../shell/mascot";
import { BrandBar } from "./brand-bar";
import { AboutLink } from "./legal-links";
import { Wordmark } from "./wordmark";

/** The four corner marks. Decoration, so they are hidden from the reader. */
export function RegistrationMarks() {
  return (
    <>
      <span className="nob-reg nob-reg-tl" aria-hidden />
      <span className="nob-reg nob-reg-tr" aria-hidden />
      <span className="nob-reg nob-reg-bl" aria-hidden />
      <span className="nob-reg nob-reg-br" aria-hidden />
    </>
  );
}

/**
 * The sheet: full-bleed ground, frame, marks, and one centred column.
 *
 * `data-pane="onboarding"` stays on it because the shell's scroll and image
 * rules key off `[data-pane]`, and because the interaction tests find this
 * screen by it.
 *
 * THE CONSOLE'S HEADER, OVER EVERY SHEET (owner, 2026-09-25; wave 88). The
 * wordmark moved out of the column into the console's own brand row, on the
 * console's translucent pane-header ground (`.nob-header`), fixed at the top.
 * None of the console's buttons come with it: there is nothing to open yet.
 * `about` puts the About link at its right. It is off where the screen already
 * has a Terms · Privacy row, which carries About instead: the sign-in, and the
 * door posters (`knock/pre-auth.tsx`), which stand in a `Page` whose footer is
 * that row.
 */
export function PosterPage({ children, about = true }: { children: ReactNode; about?: boolean }) {
  return (
    <main data-pane="onboarding" className="nob-poster">
      <header data-poster-header className="nob-header nob-poster-head">
        <BrandBar
          compact
          end={about ? <AboutLink className="nob-poster-head-link" /> : undefined}
        />
      </header>
      <RegistrationMarks />
      <div className="nob-poster-col">{children}</div>
    </main>
  );
}

/**
 * Where you are in the run, and how long it is.
 *
 * The dash-for-the-current-step is deliberate and is the reference build's: a
 * row of equal dots says "three things", a row with one of them stretched says
 * "you are two thirds of the way", which is the question somebody deciding
 * whether to start is actually asking.
 */
export function PosterStep({ step, total, label }: { step: number; total: number; label: string }) {
  return (
    <div
      data-step-dots
      aria-label={`Step ${step} of ${total} · ${label}`}
      className="nob-poster-step"
    >
      <span className="nob-poster-kicker">
        {step} of {total} · {label}
      </span>
      <span className="nob-poster-dots" aria-hidden>
        {Array.from({ length: total }, (_, i) => (
          <i key={i} data-on={i + 1 === step ? "true" : "false"} />
        ))}
      </span>
    </div>
  );
}

/**
 * The doorman on his plate — textured, because this is one of the four places
 * the grain is worth what it costs (see the note on `Grain` in shell/mascot).
 * The plate is `aria-hidden` and the face inside it keeps its own label, so a
 * screen reader hears the mood and not the furniture.
 */
export function PosterFace({ mood }: { mood: NobMood }) {
  return (
    <div className="nob-poster-hero">
      <div className="nob-poster-plate">
        <NobFace mood={mood} size="desk" textured />
      </div>
    </div>
  );
}

/** The wordmark at poster size — the plated register, not the shell's. */
export function PosterMark() {
  return (
    <div className="nob-poster-mark">
      <Wordmark plate />
    </div>
  );
}

/** A dashed box — the plate quoting something that is not its own voice. */
export function PosterBox({ kicker, children }: { kicker: string; children: ReactNode }) {
  return (
    <div className="nob-poster-box">
      <p className="nob-poster-box-kicker">{kicker}</p>
      {children}
    </div>
  );
}
